import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  Grid,
  Dialog,
  Tooltip,
  Checkbox,
  TextField,
  IconButton,
  DialogTitle,
  DialogContent,
  FormControlLabel
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import closeOutline from '@iconify-icons/eva/close-outline';
import { LoadingButton } from '@mui/lab';

import AuthUser from '../../../../../../../../common/utils/AuthUser';
import { PosBackendRest } from '@/common/utils/api';
import { customerId2Gql } from '@/common/utils/apiConversionUtils';

const useStyles: any = makeStyles(() => ({
  TextField: {
    margin: '0',
    height: '1.35em',
    borderRadius: '8px',
    color: 'currentColor',
    padding: '16.5px 14px;',
    boxSizing: 'content-box',
    letterSpacing: 'inherit',
    animationDuration: '10ms',
    border: '1px solid #dce0e4'
  }
}));

const AddNewCardModal = props => {
  const { open, setModal, customer, refreshCardList, getDefaultPayment } =
    props;
  const classes = useStyles();
  const [processing, setProcessing] = useState(false);

  const loggedInUser = AuthUser.getAuthUser();
  const storeInfo = loggedInUser?.store;

  const stripe = useStripe();
  const elements = useElements();

  const [nickname, setNickname] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);

  const handleSave = async () => {
    if (!nickname) {
      return alert('Please enter a nickname.');
    }

    if (!stripe || !elements) {
      alert(
        'Stripe has not loaded yet. Please refresh the page or contact the administrator.'
      );
    }

    setProcessing(true);
    const { error: setupIntentError, client_secret } =
      await PosBackendRest.pos_stripe.create_setup_intent({
        customer: customerId2Gql(customer?.id)
      });
    if (setupIntentError) {
      setProcessing(false);
      return alert(
        'Error creating a payment intent. This may be an issue with payment gateway. Please contact support or try again: ' +
          setupIntentError
      );
    }
    const cardElement = elements.getElement(CardElement);

    const { error, setupIntent } = await stripe.confirmCardSetup(
      client_secret,
      {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${customer?.firstName} ${customer?.lastName}`
          }
        }
      }
    );

    if (error) {
      setProcessing(false);
      return alert(
        `Error creating a recurring payment setup. This may be an issue with payment gateway. Please contact support or try again.` +
          error?.message
      );
    }

    const { paymentMethod, error: paymentMethodError } =
      await PosBackendRest.pos_stripe.add_payment_method_from_id({
        customer: customerId2Gql(customer?.id),
        store: storeInfo?.id,
        makeDefault,
        nickname,
        paymentMethod: setupIntent?.payment_method
      });
    setProcessing(false);
    if (paymentMethodError) {
      alert(paymentMethodError);
    } else if (paymentMethod) {
      setModal(false);

      refreshCardList();
    }
    getDefaultPayment();
    refreshCardList();
  };

  return (
    <Dialog
      open={open}
      onClose={() => setModal(false)}
      maxWidth="md"
      fullWidth={true}
    >
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          background: '#fafafa',
          padding: '10px 15px 10px 30px',
          justifyContent: 'space-between'
        }}
      >
        <h3>Add New Card</h3>
        <Tooltip arrow title="Close">
          <IconButton onClick={() => setModal(!open)} size="large">
            <Icon icon={closeOutline} />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} mt={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              value={nickname}
              label="Credit Card Nick Name"
              onChange={e => setNickname(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <div className={classes.TextField}>
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '20px',
                      padding: '10px 10px 0 0'
                    }
                  }
                }}
              />
            </div>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              labelPlacement="end"
              label="Make this default card"
              control={
                <Checkbox
                  color="primary"
                  checked={makeDefault}
                  onChange={() => setMakeDefault(!makeDefault)}
                />
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <div
        style={{
          width: '100%',
          padding: '10px',
          display: 'flex',
          background: '#fafafa',
          justifyContent: 'flex-end'
        }}
      >
        <LoadingButton
          type="submit"
          variant="outlined"
          loading={processing}
          onClick={handleSave}
        >
          Save
        </LoadingButton>
      </div>
    </Dialog>
  );
};

export default AddNewCardModal;
